<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Business Registration</span>
            </nav>
            <h1 class="page-title">Business Registration</h1>
            <p class="page-subtitle">Register your business and obtain a Single Business Permit</p>
          </div>
        </div>
      </section>

      <!-- Registration Form -->
      <section class="form-section">
        <div class="container">
          <div class="form-card">
            <!-- Step Progress -->
            <div class="step-progress">
              <div class="step-track">
                <div class="step-fill" :style="{ width: progressPercentage + '%' }"></div>
              </div>
              <div class="steps-container">
                <div
                    v-for="(step, index) in steps"
                    :key="index"
                    class="step-item"
                    :class="{
                      'active': currentStep === index,
                      'completed': currentStep > index
                    }"
                >
                  <div class="step-circle">
                    <svg v-if="currentStep > index" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span v-else>{{ index + 1 }}</span>
                  </div>
                  <span class="step-label">{{ step.name }}</span>
                </div>
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="message" class="alert alert-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{{ message }}</span>
              <button class="alert-close" @click="message = null">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <!-- Step 0: Year Selection -->
            <div v-if="currentStep === 0" class="form-step">
              <div class="step-header">
                <div class="step-icon year">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
                <div>
                  <h2 class="step-title">Select Permit Year</h2>
                  <p class="step-description">Choose the year for your Single Business Permit validity</p>
                </div>
              </div>

              <div class="form-grid">
                <div class="form-group">
                  <label class="form-label required">Permit Year</label>
                  <select v-model="trade.fiscalYear" class="form-select" @change="updatePermitDates">
                    <option value="">-- Select Year --</option>
                    <option v-for="(item, index) in years" :value="item.year" :key="index">
                      {{ item.year }}
                    </option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="form-label">Validity Period</label>
                  <div v-if="trade.fiscalYear" class="validity-card success">
                    <div class="validity-dates">
                      <div class="validity-date">
                        <span class="date-label">Start Date</span>
                        <span class="date-value">{{ permitStartDate }}</span>
                      </div>
                      <div class="validity-separator">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </div>
                      <div class="validity-date">
                        <span class="date-label">Expiry Date</span>
                        <span class="date-value danger">{{ permitEndDate }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="validity-card empty">
                    <span>Select a year to see validity dates</span>
                  </div>
                </div>
              </div>

              <div class="info-box">
                <h4 class="info-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  Which year should I select?
                </h4>
                <div class="info-grid">
                  <div class="info-item">
                    <span class="year-badge current">{{ currentYear }}</span>
                    <div>
                      <strong>Current Year</strong>
                      <p>Select if you need a permit for the current operating year</p>
                    </div>
                  </div>
                  <div class="info-item">
                    <span class="year-badge next">{{ nextYear }}</span>
                    <div>
                      <strong>Next Year</strong>
                      <p>Select if applying in advance for next year's permit</p>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="trade.fiscalYear" class="confirmation-box">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="yearConfirmed" class="checkbox-input" />
                  <span class="checkbox-custom"></span>
                  <span class="checkbox-text">
                    I confirm that the permit for <strong>{{ trade.fiscalYear }}</strong> will be valid from
                    <strong class="text-success">{{ permitStartDate }}</strong> and expire on
                    <strong class="text-danger">{{ permitEndDate }}</strong>
                  </span>
                </label>
              </div>
            </div>

            <!-- Step 1: Owner Information -->
            <div v-if="currentStep === 1" class="form-step">
              <div class="step-header">
                <div class="step-icon owner">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <div>
                  <h2 class="step-title">Business Owner Information</h2>
                  <p class="step-description">Provide details about the business owner</p>
                </div>
              </div>

              <div class="form-grid cols-2">
                <div class="form-group span-2">
                  <label class="form-label required">Full Name</label>
                  <input
                      v-model="trade.businessDetails.fullNames"
                      type="text"
                      class="form-input"
                      placeholder="Enter owner's full names"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label required">ID Number</label>
                  <input
                      v-model="trade.businessDetails.ownerID"
                      type="text"
                      class="form-input"
                      placeholder="Enter ID number"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label required">Phone Number</label>
                  <input
                      v-model="trade.businessDetails.ownerPhone"
                      type="tel"
                      class="form-input"
                      placeholder="e.g., 0712345678"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">Email Address</label>
                  <input
                      v-model="trade.businessDetails.ownerEmail"
                      type="email"
                      class="form-input"
                      placeholder="email@example.com"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">KRA PIN</label>
                  <input
                      v-model="trade.businessDetails.kraPin"
                      type="text"
                      class="form-input"
                      placeholder="Enter KRA PIN"
                  />
                </div>
              </div>
            </div>

            <!-- Step 2: Business Details & Location -->
            <div v-if="currentStep === 2" class="form-step">
              <div class="step-header">
                <div class="step-icon location">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div>
                  <h2 class="step-title">Business Details & Location</h2>
                  <p class="step-description">Provide business name and physical location</p>
                </div>
              </div>

              <div class="form-grid">
                <div class="form-group full-width">
                  <label class="form-label required">Business Name</label>
                  <input
                      v-model="trade.businessDetails.businessName"
                      type="text"
                      class="form-input"
                      :class="{ 'error': businessNameError }"
                      placeholder="Enter business name"
                  />
                  <span v-if="businessNameError" class="form-error">Business name is required</span>
                </div>
              </div>

              <!-- Location Status -->
              <div class="location-status" :class="locationCaptured ? 'captured' : 'required'">
                <div class="status-icon">
                  <svg v-if="locationCaptured" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
                <div class="status-content">
                  <strong v-if="locationCaptured">Location Captured Successfully</strong>
                  <strong v-else>Business Location Required</strong>
                  <p v-if="!locationCaptured">Use GPS or click on the map to set the business location</p>
                  <p v-else>Coordinates have been saved. You can proceed to the next step.</p>
                </div>
              </div>

              <!-- GPS Checkbox -->
              <div class="gps-option" :class="{ 'active': useCurrentLocation }">
                <label class="checkbox-label">
                  <input
                      type="checkbox"
                      v-model="useCurrentLocation"
                      @change="handleUseCurrentLocation"
                      class="checkbox-input"
                  />
                  <span class="checkbox-custom"></span>
                  <span class="checkbox-text">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    <strong>I am at the business location</strong>
                    <span class="text-muted">(Use my GPS coordinates)</span>
                  </span>
                </label>

                <div v-if="gettingLocation" class="gps-status loading">
                  <div class="spinner"></div>
                  <span>Getting your location...</span>
                </div>

                <div v-if="locationError" class="gps-status error">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </svg>
                  <span>{{ locationError }}</span>
                </div>

                <div v-if="locationSuccess && !gettingLocation" class="gps-status success">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span>GPS location captured!</span>
                </div>
              </div>

              <!-- Coordinates Display -->
              <div v-if="locationCaptured" class="coordinates-card">
                <div class="coordinates-header">
                  <div class="coordinates-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  </div>
                  <span>Business Location</span>
                  <button type="button" class="btn-clear" @click="clearLocation">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    Clear
                  </button>
                </div>

                <div v-if="locationAddress" class="address-display">
                  <span class="address-label">Address</span>
                  <span class="address-value">{{ locationAddress }}</span>
                </div>
                <div v-else-if="fetchingAddress" class="address-display loading">
                  <div class="spinner small"></div>
                  <span>Fetching address...</span>
                </div>

                <div class="coordinates-grid">
                  <div class="coordinate">
                    <span class="coord-label">Latitude</span>
                    <span class="coord-value">{{ trade.businessDetails.lat.toFixed(6) }}</span>
                  </div>
                  <div class="coordinate">
                    <span class="coord-label">Longitude</span>
                    <span class="coord-value">{{ trade.businessDetails.lng.toFixed(6) }}</span>
                  </div>
                </div>
              </div>

              <!-- Search Location -->
              <div class="search-location">
                <label class="form-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  Search for Location
                </label>
                <div class="search-input-wrapper">
                  <input
                      v-model="searchQuery"
                      @input="debouncedSearch"
                      @keydown.down.prevent="navigateResults('down')"
                      @keydown.up.prevent="navigateResults('up')"
                      @keydown.enter.prevent="selectHighlightedResult"
                      @keydown.escape="clearSearch"
                      @focus="showResults = true"
                      type="text"
                      class="form-input search-input"
                      placeholder="Start typing to search (e.g., Westlands, Moi Avenue...)"
                  />
                  <div v-if="searching" class="search-spinner">
                    <div class="spinner small"></div>
                  </div>
                  <button v-if="searchQuery" type="button" class="search-clear" @click="clearSearch">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                <!-- Search Results -->
                <div v-if="showResults && searchResults.length > 0" class="search-results">
                  <div
                      v-for="(result, index) in searchResults"
                      :key="index"
                      class="search-result-item"
                      :class="{ 'highlighted': highlightedIndex === index }"
                      @click="selectSearchResult(result)"
                      @mouseenter="highlightedIndex = index"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    <div class="result-content">
                      <span class="result-name">{{ getPlaceName(result) }}</span>
                      <span class="result-details">{{ getPlaceDetails(result) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Map -->
              <div class="map-container">
                <label class="form-label required">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                    <line x1="8" y1="2" x2="8" y2="18"></line>
                    <line x1="16" y1="6" x2="16" y2="22"></line>
                  </svg>
                  Click on Map to Set Location
                </label>
                <div class="map-wrapper">
                  <div v-if="mapInitializing" class="map-loading">
                    <div class="spinner large"></div>
                    <span>Loading map...</span>
                  </div>
                  <div id="leafletMap" class="map"></div>
                </div>
              </div>

              <!-- Location Details -->
              <div class="form-grid cols-3">
                <div class="form-group">
                  <label class="form-label">Sub County</label>
                  <select v-model="subCounty" class="form-select">
                    <option v-for="(item, index) in subCounties" :value="item" :key="index">
                      {{ item.subCountyName }}
                    </option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="form-label">Ward</label>
                  <select v-model="ward" class="form-select">
                    <option v-for="(item, index) in wards" :value="item" :key="index">
                      {{ item.wardName }}
                    </option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="form-label">Plot Number</label>
                  <input v-model="trade.businessDetails.plotNumber" type="text" class="form-input" placeholder="Plot No." />
                </div>

                <div class="form-group">
                  <label class="form-label">Premises Nature</label>
                  <select v-model="trade.businessDetails.buildingOccupancy" class="form-select">
                    <option value="Occupies Section In building">Section in Building</option>
                    <option value="Occupies Entire Building">Entire Building</option>
                    <option value="Mobile Business">Mobile Business</option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="form-label">Floor Number</label>
                  <select v-model="trade.businessDetails.floorNo" class="form-select">
                    <option v-for="(item, index) in trade.floors" :value="item.floor" :key="index">
                      {{ item.floor }}
                    </option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="form-label">Room/Stall No.</label>
                  <input v-model="trade.businessDetails.roomNo" type="text" class="form-input" placeholder="Room/Stall No." />
                </div>
              </div>
            </div>

            <!-- Step 3: Contact Information -->
            <div v-if="currentStep === 3" class="form-step">
              <div class="step-header">
                <div class="step-icon contact">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
                <div>
                  <h2 class="step-title">Contact Information</h2>
                  <p class="step-description">Business contact details and contact person</p>
                </div>
              </div>

              <div class="form-section-title">Business Contact</div>
              <div class="form-grid cols-3">
                <div class="form-group">
                  <label class="form-label">Business Email</label>
                  <input v-model="trade.businessDetails.businessEmail" type="email" class="form-input" placeholder="business@email.com" />
                </div>

                <div class="form-group">
                  <label class="form-label required">Postal Address</label>
                  <input v-model="trade.businessDetails.postalAddress" type="text" class="form-input" placeholder="P.O. Box" />
                </div>

                <div class="form-group">
                  <label class="form-label required">Postal Code</label>
                  <input v-model="trade.businessDetails.postalCode" type="text" class="form-input" placeholder="Postal Code" />
                </div>

                <div class="form-group">
                  <label class="form-label required">Business Phone</label>
                  <input v-model="trade.businessDetails.businessPhone" type="tel" class="form-input" placeholder="Phone number" />
                </div>

                <div class="form-group">
                  <label class="form-label required">Physical Address</label>
                  <input v-model="trade.businessDetails.physicalAddress" type="text" class="form-input" placeholder="Physical address" />
                </div>

                <div class="form-group">
                  <label class="form-label required">Building Name</label>
                  <input v-model="trade.businessDetails.buildingName" type="text" class="form-input" placeholder="Building name" />
                </div>
              </div>

              <div class="form-section-title">Contact Person</div>
              <div class="form-grid cols-3">
                <div class="form-group span-3">
                  <label class="form-label">Contact Person's Full Name</label>
                  <input v-model="trade.businessDetails.contactPersonNames" type="text" class="form-input" placeholder="Full names" />
                </div>

                <div class="form-group">
                  <label class="form-label">ID Number</label>
                  <input v-model="trade.businessDetails.contactPersonIDNo" type="text" class="form-input" placeholder="ID number" />
                </div>

                <div class="form-group">
                  <label class="form-label">Role</label>
                  <select v-model="trade.businessDetails.businessRole" class="form-select">
                    <option v-for="(item, index) in roles" :value="item.role" :key="index">{{ item.role }}</option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="form-label">Email</label>
                  <input v-model="trade.businessDetails.contactPersonEmail" type="email" class="form-input" placeholder="email@example.com" />
                </div>

                <div class="form-group">
                  <label class="form-label">Phone Number</label>
                  <input v-model="trade.businessDetails.contactPersonPhone" type="tel" class="form-input" placeholder="Phone number" />
                </div>
              </div>
            </div>

            <!-- Step 4: Business Activity -->
            <div v-if="currentStep === 4" class="form-step">
              <div class="step-header">
                <div class="step-icon activity">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                  </svg>
                </div>
                <div>
                  <h2 class="step-title">Business Activity</h2>
                  <p class="step-description">Specify your business category and activity</p>
                </div>
              </div>

              <div class="form-grid cols-2">
                <div class="form-group">
                  <label class="form-label required">Business Category</label>
                  <select @change="getTradeSubCategory($event)" class="form-select">
                    <option value="">Select category...</option>
                    <option v-for="(item, index) in trade.tradeCategories" :value="item.incomeTypeId" :key="index">
                      {{ item.incomeTypeDescription }}
                    </option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="form-label required">Business Type</label>
                  <select @change="selectedTradeSubCategory($event)" class="form-select">
                    <option value="">Select type...</option>
                    <option v-for="(item, index) in trade.tradeSubCategories" :value="JSON.stringify(item)" :key="index">
                      {{ item.feeDescription }} - KES {{ numberFormat(item.unitFeeAmount) }}
                    </option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="form-label required">Activity Description</label>
                  <input v-model="trade.businessDetails.businessDes" type="text" class="form-input" placeholder="e.g., Retail shop for general goods" />
                </div>

                <div class="form-group">
                  <label class="form-label">Premises Size (M²)</label>
                  <input v-model="trade.businessDetails.premiseSize" type="text" class="form-input" placeholder="Size in square meters" />
                </div>

                <div class="form-group">
                  <label class="form-label">Vehicle Tonnage</label>
                  <select v-model="trade.businessDetails.tonnage" class="form-select">
                    <option v-for="(item, index) in trade.tonnage" :value="item.tonnage" :key="index">{{ item.tonnage }}</option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="form-label">Number of Employees</label>
                  <input v-model="trade.businessDetails.numberOfEmployees" type="number" class="form-input" placeholder="0" />
                </div>
              </div>

              <!-- Additional Charges -->
              <div class="charges-section">
                <div class="form-section-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="16"></line>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                  </svg>
                  Other Applicable Charges
                </div>

                <div class="form-grid cols-3">
                  <div class="form-group">
                    <label class="form-label">Charge Category</label>
                    <select v-model="selectedOtherCategory" class="form-select" @change="handleCategoryChange">
                      <option :value="null">Select category...</option>
                      <option v-for="(category, index) in otherChargeCategories" :key="index" :value="category">
                        {{ category.incomeTypeDescription }}
                      </option>
                    </select>
                  </div>

                  <div class="form-group" v-if="selectedOtherCategory && tradeSubCategoriesOther.length > 0">
                    <label class="form-label">Specific Charge</label>
                    <select v-model="selectedCharge" class="form-select">
                      <option :value="null">Select charge...</option>
                      <option v-for="(charge, index) in tradeSubCategoriesOther" :key="index" :value="charge">
                        {{ charge.feeDescription }} - KES {{ numberFormat(charge.unitFeeAmount) }}
                      </option>
                    </select>
                  </div>

                  <div class="form-group add-charge-group" v-if="selectedOtherCategory && tradeSubCategoriesOther.length > 0">
                    <label class="form-label">Quantity</label>
                    <div class="add-charge-row">
                      <input v-model.number="chargeQuantity" type="number" min="1" class="form-input quantity-input" />
                      <button type="button" class="btn btn-success btn-add" @click="addChargeToList" :disabled="!selectedCharge">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        Add
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Added Charges Table -->
                <div v-if="addedCharges.length > 0" class="charges-table">
                  <table>
                    <thead>
                    <tr>
                      <th>Description</th>
                      <th class="text-center">Qty</th>
                      <th class="text-right">Unit Price</th>
                      <th class="text-right">Total</th>
                      <th class="text-center">Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item, index) in addedCharges" :key="index">
                      <td>{{ item.feeDescription }}</td>
                      <td class="text-center">{{ item.quantity }}</td>
                      <td class="text-right">KES {{ numberFormat(item.unitFeeAmount) }}</td>
                      <td class="text-right fw-bold">KES {{ numberFormat(item.unitFeeAmount * item.quantity) }}</td>
                      <td class="text-center">
                        <button type="button" class="btn-remove" @click="removeCharge(index)">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>

                <div v-else class="no-charges">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  <span>No additional charges added yet</span>
                </div>

                <!-- Penalty Checkbox -->
                <div class="penalty-option">
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="penalized" class="checkbox-input" />
                    <span class="checkbox-custom"></span>
                    <span class="checkbox-text">Apply penalty charges (if applicable)</span>
                  </label>
                </div>
              </div>

              <!-- Total Amount -->
              <div class="total-card">
                <div class="total-header">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                  <span>Payment Summary</span>
                </div>
                <div class="total-breakdown">
                  <div class="total-row">
                    <span>Base Permit Fee</span>
                    <span>KES {{ numberFormat(trade.amount || 0) }}</span>
                  </div>
                  <div class="total-row">
                    <span>Additional Charges</span>
                    <span>KES {{ numberFormat(totalAddedCharges) }}</span>
                  </div>
                </div>
                <div class="total-final">
                  <span>Total Amount</span>
                  <span class="total-amount">KES {{ numberFormat(totalComputedAmount) }}</span>
                </div>
              </div>
            </div>

            <!-- Navigation Buttons -->
            <div class="form-navigation">
              <button
                  v-if="currentStep > 0"
                  type="button"
                  class="btn btn-secondary"
                  @click="handlePrevClick"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                Previous
              </button>
              <div v-else></div>

              <button
                  v-if="currentStep < steps.length - 1"
                  type="button"
                  class="btn btn-primary"
                  @click="handleNextClick"
                  :disabled="isNextDisabled"
              >
                Next
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </button>

              <button
                  v-if="currentStep === steps.length - 1"
                  type="button"
                  class="btn btn-success"
                  @click="registerBusiness"
                  :disabled="loading"
              >
                <svg v-if="!loading" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <div v-else class="spinner small white"></div>
                {{ loading ? 'Submitting...' : 'Submit Application' }}
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Success Modal -->
    <transition name="modal">
      <div v-if="showSuccessPopup" class="modal-overlay" @click.self="closeSuccessPopup">
        <div class="modal-container success-modal">
          <div class="success-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <h2 class="success-title">Application Submitted!</h2>
          <p class="success-message">Your business registration application has been received successfully.</p>

          <div class="business-id-card">
            <span class="id-label">Your Business ID</span>
            <span class="id-value">{{ businessID }}</span>
          </div>

          <p class="success-note">
            Once your application is approved, you will receive an invoice via SMS/Email to make the payment for your business permit.
          </p>

          <div class="success-actions">
            <button class="btn btn-primary" @click="closeSuccessPopup">
              OK, Got it
            </button>
            <button class="btn btn-secondary" @click="registerAnother">
              Register Another
            </button>
          </div>
        </div>
      </div>
    </transition>

    <FooterComponent />
  </div>
</template>

<script>
/* eslint-disable */
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import { biller, trade, execute } from "@/api";

export default {
  name: "CreateBusiness",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      loading: false,
      showSuccessPopup: false,
      message: null,
      currentStep: 0,
      steps: [
        { name: 'Year', icon: 'calendar' },
        { name: 'Owner', icon: 'user' },
        { name: 'Location', icon: 'map' },
        { name: 'Contact', icon: 'phone' },
        { name: 'Activity', icon: 'briefcase' }
      ],

      // Year selection
      yearConfirmed: false,
      permitStartDate: '',
      permitEndDate: '',

      // Location
      map: null,
      marker: null,
      mapInitializing: false,
      useCurrentLocation: false,
      gettingLocation: false,
      locationError: null,
      locationSuccess: false,
      locationAddress: '',
      fetchingAddress: false,
      businessNameError: false,

      // Search
      searchQuery: '',
      searchResults: [],
      searching: false,
      showResults: false,
      highlightedIndex: -1,
      searchDebounceTimer: null,

      // Other charges
      selectedOtherCategory: null,
      selectedCharge: null,
      chargeQuantity: 1,
      addedCharges: [],
      showAllCharges: false,
      penalized: false,

      // Trade data
      amount: 0,
      unitFeeAmount: 0,
      wardID: '',
      subCountyID: '',
      subCounties: [{ subCountyID: "", subCountyName: "" }],
      wards: [{ wardID: "", wardName: "" }],
      businessID: null,
      subCounty: {},
      ward: {},

      // Years & Roles
      years: [],
      roles: [],

      // Other charge categories
      otherChargeCategories: [],
      tradeSubCategoriesOther: [],

      trade: {
        fiscalYear: '',
        amount: null,
        tonnage: [],
        floors: [],
        tradeCategories: [],
        tradeSubCategories: [],
        businessDetails: {
          id: null, businessID: null, businessName: null, subCountyID: null, subCountyName: null,
          wardID: null, wardName: null, plotNumber: null, physicalAddress: null, buildingName: null,
          buildingOccupancy: null, floorNo: null, roomNo: null, premiseSize: null, numberOfEmployees: null,
          tonnage: null, businessDes: null, businessCategory: null, businessSubCategory: null,
          incomeTypeID: null, feeID: null, businessEmail: null, postalAddress: null, postalCode: null,
          businessPhone: null, contactPersonNames: null, contactPersonIDNo: null, businessRole: null,
          contactPersonPhone: null, contactPersonEmail: null, fullNames: null, ownerID: null,
          ownerPhone: null, ownerEmail: null, kraPin: null, createdBy: null, createdByIDNo: null,
          dateCreated: null, lat: 0.0, lng: 0.0
        }
      }
    };
  },

  computed: {
    currentYear() { return new Date().getFullYear(); },
    nextYear() { return new Date().getFullYear() + 1; },
    progressPercentage() { return (this.currentStep / (this.steps.length - 1)) * 100; },
    locationCaptured() {
      const lat = this.trade.businessDetails.lat;
      const lng = this.trade.businessDetails.lng;
      const isDefault = (lat === -1.286389 && lng === 36.817223);
      return lat && lng && lat !== 0 && lng !== 0 && !isDefault;
    },
    isNextDisabled() {
      if (this.currentStep === 0) return !this.trade.fiscalYear || !this.yearConfirmed;
      if (this.currentStep === 2) {
        const hasName = this.trade.businessDetails.businessName?.trim();
        return !hasName || !this.locationCaptured;
      }
      return false;
    },
    totalAddedCharges() {
      return this.addedCharges.reduce((total, item) => total + (item.unitFeeAmount * item.quantity), 0);
    },
    totalComputedAmount() {
      return (parseFloat(this.trade.amount) || 0) + this.totalAddedCharges;
    }
  },

  watch: {
    subCounty(val) {
      if (val?.subCountyID) {
        this.trade.businessDetails.subCountyName = val.subCountyName;
        this.trade.businessDetails.subCountyID = val.subCountyID;
        this.getWards();
      }
    },
    ward(val) {
      if (val?.wardID) {
        this.trade.businessDetails.wardName = val.wardName;
        this.trade.businessDetails.wardID = val.wardID;
      }
    },
    'trade.fiscalYear'(newVal, oldVal) {
      if (newVal) {
        this.updatePermitDates();
        if (oldVal && newVal !== oldVal) this.yearConfirmed = false;
      } else {
        this.yearConfirmed = false;
      }
    },
    'trade.businessDetails.businessName'(val) {
      if (val?.trim()) this.businessNameError = false;
    },
    currentStep(newStep) {
      if (newStep === 2) {
        this.$nextTick(() => setTimeout(() => this.initMapWithRetry(), 300));
      }
    }
  },

  mounted() {
    this.getTradeCategory();
    this.getFloor();
    this.getSubCounties();
    this.getTonnage();
    this.getYears();
    this.getRoles();
    this.getTradeCategoryOther();
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    if (this.map) { this.map.remove(); this.map = null; }
    this.marker = null;
  },

  methods: {
    numberFormat(num) { return new Intl.NumberFormat("en-GB").format(num); },

    sanitizeAddress(address) {
      if (!address || typeof address !== 'string') return address;
      return address.replace(/['′'`]/g, '').replace(/["″"]/g, '').replace(/\\/g, '').trim();
    },

    updatePermitDates() {
      if (this.trade.fiscalYear) {
        const year = parseInt(this.trade.fiscalYear);
        this.permitStartDate = `1st January ${year}`;
        this.permitEndDate = `31st December ${year}`;
      }
    },

    handleNextClick() {
      if (this.currentStep === 0 && !this.trade.fiscalYear) {
        this.message = 'Please select a permit year';
        return;
      }
      if (this.currentStep === 0 && !this.yearConfirmed) {
        this.message = 'Please confirm the permit validity period';
        return;
      }
      if (this.currentStep === 2) {
        if (!this.trade.businessDetails.businessName?.trim()) {
          this.businessNameError = true;
          this.message = 'Please enter the business name';
          return;
        }
        if (!this.locationCaptured) {
          this.message = 'Please provide the business location';
          return;
        }
      }
      this.message = null;
      this.currentStep++;
    },

    handlePrevClick() {
      if (this.currentStep > 0) this.currentStep--;
    },

    clearLocation() {
      this.trade.businessDetails.lat = 0;
      this.trade.businessDetails.lng = 0;
      this.useCurrentLocation = false;
      this.locationSuccess = false;
      this.locationError = null;
      this.locationAddress = '';
      this.searchQuery = '';
      this.searchResults = [];

      if (this.map && this.marker) {
        this.map.setView([-1.286389, 36.817223], 13);
        this.marker.setLatLng([-1.286389, 36.817223]);
      }
    },

    handleUseCurrentLocation() {
      if (this.useCurrentLocation) this.getCurrentLocation();
      else { this.locationSuccess = false; this.locationError = null; }
    },

    getCurrentLocation() {
      if (!navigator.geolocation) {
        this.locationError = 'Geolocation not supported';
        this.useCurrentLocation = false;
        return;
      }

      this.gettingLocation = true;
      this.locationError = null;
      this.locationSuccess = false;

      navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            this.trade.businessDetails.lat = lat;
            this.trade.businessDetails.lng = lng;

            if (this.map && this.marker) {
              this.map.setView([lat, lng], 17);
              this.marker.setLatLng([lat, lng]);
            }

            this.gettingLocation = false;
            this.locationSuccess = true;
            this.reverseGeocode(lat, lng);
          },
          (error) => {
            this.locationError = error.code === 1 ? 'Location access denied' : 'Could not get location';
            this.gettingLocation = false;
            this.useCurrentLocation = false;
          },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    },

    updateCoordinates(lat, lng) {
      this.trade.businessDetails.lat = lat;
      this.trade.businessDetails.lng = lng;
      this.locationSuccess = true;
      this.locationError = null;
      this.reverseGeocode(lat, lng);
      setTimeout(() => { if (!this.gettingLocation) this.locationSuccess = false; }, 3000);
    },

    async reverseGeocode(lat, lng) {
      this.fetchingAddress = true;
      this.locationAddress = '';
      try {
        const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
            { headers: { 'Accept-Language': 'en', 'User-Agent': 'SBPApplication/1.0' } }
        );
        if (response.ok) {
          const data = await response.json();
          if (data?.display_name) {
            this.locationAddress = this.sanitizeAddress(data.display_name);
            this.trade.businessDetails.physicalAddress = this.locationAddress;
          }
        }
      } catch (e) { console.error(e); }
      finally { this.fetchingAddress = false; }
    },

    debouncedSearch() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.highlightedIndex = -1;
      if (!this.searchQuery || this.searchQuery.trim().length < 2) {
        this.searchResults = [];
        this.searching = false;
        return;
      }
      this.searching = true;
      this.showResults = true;
      this.searchDebounceTimer = setTimeout(() => this.performSearch(), 300);
    },

    async performSearch() {
      if (!this.searchQuery || this.searchQuery.trim().length < 2) {
        this.searchResults = [];
        this.searching = false;
        return;
      }
      try {
        const query = this.searchQuery.toLowerCase().includes('kenya') ? this.searchQuery : `${this.searchQuery}, Kenya`;
        const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=8&countrycodes=ke&addressdetails=1`,
            { headers: { 'Accept-Language': 'en', 'User-Agent': 'SBPApplication/1.0' } }
        );
        if (response.ok) {
          this.searchResults = (await response.json()) || [];
          this.highlightedIndex = this.searchResults.length > 0 ? 0 : -1;
        }
      } catch (e) { this.searchResults = []; }
      finally { this.searching = false; }
    },

    getPlaceName(result) {
      return result.address?.amenity || result.address?.building || result.address?.road || result.display_name.split(',')[0];
    },

    getPlaceDetails(result) {
      const parts = result.display_name.split(',');
      return parts.length > 1 ? parts.slice(1, 4).join(',').trim() : '';
    },

    navigateResults(direction) {
      if (this.searchResults.length === 0) return;
      if (direction === 'down') this.highlightedIndex = (this.highlightedIndex + 1) % this.searchResults.length;
      else this.highlightedIndex = this.highlightedIndex <= 0 ? this.searchResults.length - 1 : this.highlightedIndex - 1;
    },

    selectHighlightedResult() {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.searchResults.length) {
        this.selectSearchResult(this.searchResults[this.highlightedIndex]);
      }
    },

    clearSearch() {
      this.searchQuery = '';
      this.searchResults = [];
      this.showResults = false;
      this.highlightedIndex = -1;
    },

    handleClickOutside(event) {
      const dropdown = document.querySelector('.search-results');
      const input = document.querySelector('.search-input');
      if (dropdown && input && !dropdown.contains(event.target) && !input.contains(event.target)) {
        this.showResults = false;
      }
    },

    selectSearchResult(result) {
      const lat = parseFloat(result.lat);
      const lng = parseFloat(result.lon);
      this.trade.businessDetails.lat = lat;
      this.trade.businessDetails.lng = lng;
      this.locationAddress = this.sanitizeAddress(result.display_name);
      this.trade.businessDetails.physicalAddress = this.locationAddress;

      if (this.map && this.marker) {
        this.map.setView([lat, lng], 17);
        this.marker.setLatLng([lat, lng]);
      }

      this.clearSearch();
      this.locationSuccess = true;
      setTimeout(() => { this.locationSuccess = false; }, 3000);
    },

    initMapWithRetry(attempts = 0) {
      if (attempts >= 5) return;
      const mapContainer = document.getElementById('leafletMap');
      if (!mapContainer || mapContainer.getBoundingClientRect().width === 0) {
        setTimeout(() => this.initMapWithRetry(attempts + 1), 500);
        return;
      }
      this.initLeafletMap();
    },

    initLeafletMap() {
      if (this.map) { setTimeout(() => this.map.invalidateSize(), 100); return; }
      this.mapInitializing = true;

      const initMap = () => {
        try {
          this.map = L.map('leafletMap').setView([-1.286389, 36.817223], 13);
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '© OpenStreetMap'
          }).addTo(this.map);

          this.marker = L.marker([-1.286389, 36.817223], { draggable: true }).addTo(this.map);
          this.marker.bindPopup('Click to set location').openPopup();

          this.marker.on('dragend', (e) => {
            const pos = e.target.getLatLng();
            this.updateCoordinates(pos.lat, pos.lng);
          });

          this.map.on('click', (e) => {
            this.marker.setLatLng(e.latlng);
            this.updateCoordinates(e.latlng.lat, e.latlng.lng);
          });

          setTimeout(() => this.map?.invalidateSize(), 300);
          this.mapInitializing = false;
        } catch (e) { console.error(e); this.mapInitializing = false; }
      };

      if (typeof L !== 'undefined') setTimeout(initMap, 100);
      else {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => setTimeout(initMap, 100);
        document.head.appendChild(script);
      }
    },

    handleCategoryChange() {
      this.selectedCharge = null;
      if (this.selectedOtherCategory) this.getTradeSubCategoryOther(this.selectedOtherCategory.incomeTypeId);
    },

    addChargeToList() {
      if (!this.selectedCharge) return;
      const existing = this.addedCharges.findIndex(item => item.feeId === this.selectedCharge.feeId);
      if (existing !== -1) this.addedCharges[existing].quantity += this.chargeQuantity;
      else this.addedCharges.push({ ...this.selectedCharge, quantity: this.chargeQuantity });
      this.selectedCharge = null;
      this.chargeQuantity = 1;
    },

    removeCharge(index) { this.addedCharges.splice(index, 1); },

    closeSuccessPopup() { this.showSuccessPopup = false; this.$router.push('/'); },

    registerAnother() { this.showSuccessPopup = false; this.resetForm(); },

    resetForm() {
      this.currentStep = 0;
      this.yearConfirmed = false;
      this.businessID = null;
      this.addedCharges = [];
      this.selectedOtherCategory = null;
      this.trade.fiscalYear = '';
      this.trade.amount = null;
      Object.keys(this.trade.businessDetails).forEach(key => {
        this.trade.businessDetails[key] = key === 'lat' || key === 'lng' ? 0 : null;
      });
    },

    registerBusiness() {
      if (!this.trade.fiscalYear) { this.message = "Select a fiscal year"; return; }
      if (!this.trade.businessDetails.fullNames?.trim()) { this.message = "Enter owner's name"; return; }
      if (!this.trade.businessDetails.ownerID?.trim()) { this.message = "Enter owner's ID"; return; }
      if (!this.trade.businessDetails.ownerPhone?.trim()) { this.message = "Enter owner's phone"; return; }
      if (!this.trade.businessDetails.businessName?.trim()) { this.message = "Enter business name"; return; }
      if (!this.locationCaptured) { this.message = "Provide business location"; return; }
      if (!this.trade.businessDetails.feeID) { this.message = "Select business category"; return; }

      this.message = null;
      this.loading = true;

      const data = new FormData();
      data.append("function", "registerBusiness");
      data.append("type", "SELF");
      data.append("business", JSON.stringify(this.trade.businessDetails));
      data.append("fiscalYear", this.trade.fiscalYear);
      if (this.addedCharges.length > 0) data.append("additionalCharges", JSON.stringify(this.addedCharges));
      if (this.penalized) data.append("penalized", this.penalized);

      execute(data, trade)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.businessID = res.data.data.business.businessID;
              this.showSuccessPopup = true;
            } else this.message = res.data.message;
          })
          .catch((e) => { this.loading = false; this.message = e.message; });
    },

    // API Methods
    getFloor() {
      const data = new FormData();
      data.append("function", "getFloor");
      execute(data, trade).then(res => { if (res.data.success) this.trade.floors = res.data.data.floors; });
    },

    getTonnage() {
      const data = new FormData();
      data.append("function", "getTonnage");
      execute(data, trade).then(res => { if (res.data.success) this.trade.tonnage = res.data.data.tonnage; });
    },

    getTradeCategory() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");
      data.append("incomeTypePrefix", "SBP");
      execute(data, biller).then(res => { if (res.data.success) this.trade.tradeCategories = res.data.data.incomeTypes; });
    },

    getYears() {
      const data = new FormData();
      data.append("function", "getYears");
      execute(data, biller).then(res => { if (res.data.success) this.years = res.data.data.years; });
    },

    getRoles() {
      const data = new FormData();
      data.append("function", "getRoles");
      execute(data, trade).then(res => { if (res.data.success) this.roles = res.data.data.roles; });
    },

    getTradeSubCategory(event) {
      this.trade.businessDetails.incomeTypeID = event.target.value;
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", event.target.value);
      execute(data, biller).then(res => { if (res.data.success) this.trade.tradeSubCategories = res.data.data.feesAndCharges; });
    },

    selectedTradeSubCategory(event) {
      const data = JSON.parse(event.target.value);
      this.trade.businessDetails.businessCategory = data.accountDesc;
      this.trade.businessDetails.businessSubCategory = data.feeDescription;
      this.trade.amount = data.unitFeeAmount;
      this.trade.businessDetails.feeID = data.feeId;
    },

    getTradeCategoryOther(forceAll = false) {
      const data = new FormData();
      data.append("function", "getIncomeTypes");
      data.append("incomeTypePrefix", "SBP");
      if (!forceAll) data.append("keyword", "Other");
      execute(data, biller).then(res => {
        const types = res.data?.data?.incomeTypes || [];
        if (types.length === 0 && !forceAll) { this.showAllCharges = true; this.getTradeCategoryOther(true); return; }
        this.otherChargeCategories = types;
      });
    },

    getTradeSubCategoryOther(incomeTypeId) {
      this.tradeSubCategoriesOther = [];
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);
      execute(data, biller).then(res => { if (res.data.success) this.tradeSubCategoriesOther = res.data.data.feesAndCharges; });
    },

    getSubCounties() {
      const data = new FormData();
      data.append("function", "getSubCounty");
      execute(data, biller).then(res => { if (res.data.success) this.subCounties = res.data.data.subCounties; });
    },

    getWards() {
      this.wards = [];
      this.ward = '';
      const data = new FormData();
      data.append("function", "getWards");
      data.append("subCountyID", this.subCounty.subCountyID);
      execute(data, biller).then(res => { if (res.data.success) this.wards = res.data.data.wards; });
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 16px;
}

/* Form Section */
.form-section {
  padding: 48px 0 80px;
  background: #f8fafc;
}

.form-card {
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

/* Step Progress */
.step-progress {
  margin-bottom: 40px;
}

.step-track {
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin-bottom: 24px;
  overflow: hidden;
}

.step-fill {
  height: 100%;
  background: #00a86b;
  transition: width 0.3s ease;
}

.steps-container {
  display: flex;
  justify-content: space-between;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  background: #e2e8f0;
  color: #64748b;
  transition: all 0.3s;
}

.step-item.active .step-circle {
  background: #0f4c81;
  color: white;
}

.step-item.completed .step-circle {
  background: #00a86b;
  color: white;
}

.step-label {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}

.step-item.active .step-label { color: #0f4c81; font-weight: 600; }
.step-item.completed .step-label { color: #00a86b; }

/* Alert */
.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 10px;
  margin-bottom: 24px;
}

.alert-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: inherit;
}

/* Form Step */
.form-step {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 32px;
}

.step-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.step-icon.year { background: #fef3c7; color: #d97706; }
.step-icon.owner { background: #dbeafe; color: #2563eb; }
.step-icon.location { background: #d1fae5; color: #059669; }
.step-icon.contact { background: #fce7f3; color: #db2777; }
.step-icon.activity { background: #e0e7ff; color: #4f46e5; }

.step-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.step-description {
  color: #64748b;
  font-size: 14px;
}

/* Form Grid */
.form-grid {
  display: grid;
  gap: 20px;
}

.form-grid.cols-2 { grid-template-columns: repeat(2, 1fr); }
.form-grid.cols-3 { grid-template-columns: repeat(3, 1fr); }

.form-group.span-2 { grid-column: span 2; }
.form-group.span-3 { grid-column: span 3; }
.form-group.full-width { grid-column: 1 / -1; }

.form-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.form-label.required::after {
  content: '*';
  color: #dc2626;
}

.form-input, .form-select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
}

.form-input:focus, .form-select:focus {
  outline: none;
  border-color: #0f4c81;
}

.form-input.error { border-color: #dc2626; }

.form-error {
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
}

/* Validity Card */
.validity-card {
  padding: 16px;
  border-radius: 10px;
  border: 2px solid;
}

.validity-card.success {
  background: #f0fdf4;
  border-color: #86efac;
}

.validity-card.empty {
  background: #f8fafc;
  border-color: #e2e8f0;
  color: #64748b;
  text-align: center;
}

.validity-dates {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.validity-date {
  display: flex;
  flex-direction: column;
}

.date-label { font-size: 12px; color: #64748b; }
.date-value { font-weight: 600; color: #1e293b; }
.date-value.danger { color: #dc2626; }
.validity-separator { color: #64748b; }

/* Info Box */
.info-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  margin-top: 24px;
}

.info-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1e293b;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.info-item {
  display: flex;
  gap: 12px;
}

.year-badge {
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.year-badge.current { background: #dbeafe; color: #2563eb; }
.year-badge.next { background: #d1fae5; color: #059669; }

.info-item p { font-size: 13px; color: #64748b; margin: 4px 0 0; }

/* Confirmation Box */
.confirmation-box {
  margin-top: 24px;
  padding: 20px;
  background: #eff6ff;
  border: 2px solid #bfdbfe;
  border-radius: 12px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
}

.checkbox-input { display: none; }

.checkbox-custom {
  width: 22px;
  height: 22px;
  border: 2px solid #cbd5e1;
  border-radius: 6px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin-top: 2px;
}

.checkbox-input:checked + .checkbox-custom {
  background: #0f4c81;
  border-color: #0f4c81;
}

.checkbox-input:checked + .checkbox-custom::after {
  content: '✓';
  color: white;
  font-size: 14px;
  font-weight: bold;
}

.checkbox-text { font-size: 14px; line-height: 1.5; }
.text-success { color: #059669; }
.text-danger { color: #dc2626; }
.text-muted { color: #64748b; }

/* Location Status */
.location-status {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 24px;
}

.location-status.required {
  background: #fef2f2;
  border: 2px solid #fecaca;
}

.location-status.captured {
  background: #f0fdf4;
  border: 2px solid #86efac;
}

.status-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.location-status.required .status-icon { background: #fee2e2; color: #dc2626; }
.location-status.captured .status-icon { background: #dcfce7; color: #16a34a; }

.status-content strong { font-size: 16px; display: block; margin-bottom: 4px; }
.status-content p { font-size: 14px; color: #64748b; margin: 0; }

/* GPS Option */
.gps-option {
  padding: 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 24px;
  transition: all 0.2s;
}

.gps-option.active {
  border-color: #86efac;
  background: #f0fdf4;
}

.gps-option .checkbox-text {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.gps-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
}

.gps-status.loading { background: #eff6ff; color: #2563eb; }
.gps-status.error { background: #fef2f2; color: #dc2626; }
.gps-status.success { background: #f0fdf4; color: #16a34a; }

/* Coordinates Card */
.coordinates-card {
  background: #f0fdf4;
  border: 2px solid #86efac;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.coordinates-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.coordinates-icon {
  width: 40px;
  height: 40px;
  background: #dcfce7;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #16a34a;
}

.coordinates-header span { font-weight: 600; color: #16a34a; }

.btn-clear {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #fecaca;
  border-radius: 6px;
  font-size: 13px;
  color: #dc2626;
  cursor: pointer;
}

.address-display {
  background: white;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.address-label { font-size: 12px; color: #64748b; display: block; }
.address-value { font-weight: 500; }

.address-display.loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
}

.coordinates-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.coordinate {
  background: white;
  padding: 12px;
  border-radius: 8px;
}

.coord-label { font-size: 12px; color: #64748b; display: block; }
.coord-value { font-weight: 600; font-family: monospace; }

/* Search Location */
.search-location {
  margin-bottom: 24px;
  position: relative;
}

.search-input-wrapper {
  position: relative;
}

.search-input { padding-right: 80px; }

.search-spinner {
  position: absolute;
  right: 48px;
  top: 50%;
  transform: translateY(-50%);
}

.search-clear {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #64748b;
}

.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 100;
  margin-top: 4px;
}

.search-result-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
}

.search-result-item:hover, .search-result-item.highlighted {
  background: #eff6ff;
}

.search-result-item svg { color: #dc2626; flex-shrink: 0; margin-top: 2px; }
.result-name { font-weight: 500; display: block; }
.result-details { font-size: 13px; color: #64748b; }

/* Map */
.map-container { margin-bottom: 24px; }

.map-wrapper {
  position: relative;
  height: 350px;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #e2e8f0;
}

.map-loading {
  position: absolute;
  inset: 0;
  background: rgba(255,255,255,0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 1000;
}

.map { width: 100%; height: 100%; }

/* Spinner */
.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #e2e8f0;
  border-top-color: #0f4c81;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner.small { width: 16px; height: 16px; border-width: 2px; }
.spinner.large { width: 40px; height: 40px; border-width: 4px; }
.spinner.white { border-color: rgba(255,255,255,0.3); border-top-color: white; }

@keyframes spin { to { transform: rotate(360deg); } }

/* Form Section Title */
.form-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 32px 0 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
}

/* Charges Section */
.charges-section {
  margin-top: 32px;
  padding: 24px;
  background: #f8fafc;
  border-radius: 12px;
}

.add-charge-group .add-charge-row {
  display: flex;
  gap: 12px;
}

.quantity-input { width: 80px !important; flex-shrink: 0; }

.btn-add {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.charges-table {
  margin-top: 20px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.charges-table table {
  width: 100%;
  border-collapse: collapse;
}

.charges-table th, .charges-table td {
  padding: 12px 16px;
  text-align: left;
}

.charges-table thead { background: #f1f5f9; }
.charges-table th { font-size: 12px; font-weight: 600; text-transform: uppercase; color: #64748b; }
.charges-table tbody tr { border-top: 1px solid #e2e8f0; }
.charges-table tbody tr:hover { background: #f8fafc; }

.text-center { text-align: center; }
.text-right { text-align: right; }
.fw-bold { font-weight: 600; }

.btn-remove {
  background: #fee2e2;
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  color: #dc2626;
}

.no-charges {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  color: #64748b;
  font-size: 14px;
}

.penalty-option {
  margin-top: 20px;
  padding: 16px;
  background: white;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

/* Total Card */
.total-card {
  margin-top: 32px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 12px;
  padding: 24px;
  border: 2px solid #fbbf24;
}

.total-header {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 16px;
}

.total-breakdown {
  background: rgba(255,255,255,0.6);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
  color: #78350f;
}

.total-final {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-final span:first-child { font-weight: 600; color: #78350f; }
.total-amount { font-size: 28px; font-weight: 800; color: #92400e; }

/* Navigation */
.form-navigation {
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-primary {
  background: #0f4c81;
  color: white;
}

.btn-primary:hover:not(:disabled) { background: #0a3459; }

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover { background: #e2e8f0; }

.btn-success {
  background: #059669;
  color: white;
}

.btn-success:hover:not(:disabled) { background: #047857; }

.btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* Success Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 480px;
  width: 100%;
}

.success-modal { text-align: center; }

.success-icon {
  width: 80px;
  height: 80px;
  background: #d1fae5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: #059669;
}

.success-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #059669;
  margin-bottom: 12px;
}

.success-message {
  color: #64748b;
  margin-bottom: 24px;
}

.business-id-card {
  background: #eff6ff;
  border: 2px solid #bfdbfe;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.id-label { font-size: 12px; color: #64748b; display: block; margin-bottom: 4px; }
.id-value { font-size: 24px; font-weight: 700; color: #0f4c81; font-family: monospace; }

.success-note {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 24px;
}

.success-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

/* Transitions */
.modal-enter-active, .modal-leave-active { transition: all 0.3s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-from .modal-container, .modal-leave-to .modal-container {
  transform: scale(0.95) translateY(20px);
}

/* Responsive */
@media (max-width: 768px) {
  .form-card { padding: 24px; }
  .form-grid.cols-2, .form-grid.cols-3 { grid-template-columns: 1fr; }
  .form-group.span-2, .form-group.span-3 { grid-column: span 1; }
  .steps-container { gap: 8px; }
  .step-label { display: none; }
  .info-grid { grid-template-columns: 1fr; }
  .validity-dates { flex-direction: column; gap: 12px; }
  .validity-separator { transform: rotate(90deg); }
  .success-actions { flex-direction: column; }
}
</style>